/**
 * FEATURE A4 · UndoToast · 2026-05-21.
 *
 * Bottom-center toast (above safe-area on mobile) com 30s countdown
 * visual e botão "Desfazer". Clica → POST `/v1/undo/:eventId`. Auto-
 * dismiss em 30s ou quando outra undo toast aparece (single-slot).
 *
 * Visual:
 *   - Pill arredondado, surface elev-2 com border line.
 *   - Label do item + contador "(28s)" decrementando.
 *   - Botão "Desfazer" primary; "✕" fechar à direita.
 *
 * Acessibilidade:
 *   - role="status" / aria-live="polite" (announce mas não interrompe)
 *   - Foco fica no botão "Desfazer" pra Enter resolver direto.
 *
 * Provider único montado no App root (`<UndoToastHost />`).
 */

import { useEffect, useMemo, useState } from 'react';
import { api } from '@/api/client';
import { useUndoToast } from '@/store/undo-toast.store';
import { cn } from '@/lib/cn';
import { useT } from '@/hooks/useT';

const TOAST_DURATION_MS = 30_000;

/**
 * Single mount point — renders the current toast or nothing. Place in
 * App root after RouterProvider.
 */
export function UndoToastHost() {
  const current = useUndoToast((s) => s.current);
  const dismiss = useUndoToast((s) => s.dismiss);

  if (!current) return null;

  return (
    <UndoToastView
      key={current.id}
      toast={current}
      onAutoDismiss={dismiss}
      onManualDismiss={dismiss}
    />
  );
}

function UndoToastView({
  toast,
  onAutoDismiss,
  onManualDismiss,
}: {
  toast: NonNullable<ReturnType<typeof useUndoToast.getState>['current']>;
  onAutoDismiss: () => void;
  onManualDismiss: () => void;
}) {
  const { t } = useT();
  const tUndo = t.undoToast;
  const [now, setNow] = useState(() => Date.now());
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Tick once per second for the countdown. Throttled — only re-renders
  // when the visible "Xs" digit changes.
  useEffect(() => {
    const tick = setInterval(() => setNow(Date.now()), 1_000);
    return () => clearInterval(tick);
  }, []);

  const elapsed = now - toast.postedAt;
  const remainingMs = Math.max(0, TOAST_DURATION_MS - elapsed);
  const remainingSecs = Math.ceil(remainingMs / 1000);

  // Auto-dismiss on countdown end.
  useEffect(() => {
    if (remainingMs <= 0) {
      onAutoDismiss();
    }
  }, [remainingMs, onAutoDismiss]);

  async function handleUndo() {
    if (submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      const res = await api.undoEvent(toast.eventId);
      // Notify the calling component (cache invalidation etc).
      toast.onUndone?.();
      if (res.alreadyRestored) {
        // Cosmetic: usuário acionou 2 undos rápido, segunda foi noop.
        // Fechamos toast normalmente.
      }
      onManualDismiss();
    } catch (err) {
      // Mostra o erro inline; toast NÃO some sozinho aqui pra usuário
      // ver. Usa o `message` do ApiError (já humanizado por backend
      // quando undo_not_supported).
      const msg = err instanceof Error ? err.message : 'Erro ao desfazer.';
      setError(msg);
      setSubmitting(false);
    }
  }

  // Pre-compute label + counter pra evitar re-render churn.
  const labelWithCounter = useMemo(
    () => tUndo.labelWithCounter(toast.label, remainingSecs),
    [tUndo, toast.label, remainingSecs],
  );

  return (
    <div
      role="status"
      aria-live="polite"
      data-testid="undo-toast"
      className={cn(
        // Posicionamento: bottom-center, above safe-area no mobile
        // (env(safe-area-inset-bottom) cobre notch / home indicator).
        'fixed inset-x-0 z-[60] flex justify-center px-4',
        'bottom-[max(env(safe-area-inset-bottom),16px)]',
        'pointer-events-none',
      )}
    >
      <div
        className={cn(
          'pointer-events-auto flex max-w-md items-center gap-3 rounded-full',
          'border border-line bg-surface-2/95 px-4 py-2 backdrop-blur elev-2',
          'text-[13px] text-ink',
        )}
      >
        <span data-testid="undo-toast-label" className="flex-1 truncate">
          {error ?? labelWithCounter}
        </span>
        {!error && (
          <button
            type="button"
            onClick={handleUndo}
            disabled={submitting}
            data-testid="undo-toast-action"
            autoFocus
            className={cn(
              'shrink-0 inline-flex min-h-[32px] items-center rounded-full px-3',
              'text-[12px] font-semibold text-brand-600',
              'hover:bg-brand-500/10 disabled:opacity-50',
              'focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-400',
            )}
          >
            {submitting ? tUndo.submitting : tUndo.action}
          </button>
        )}
        <button
          type="button"
          onClick={onManualDismiss}
          aria-label={tUndo.dismiss}
          data-testid="undo-toast-close"
          className={cn(
            'shrink-0 inline-flex h-7 w-7 items-center justify-center rounded-full',
            'text-ink-subtle hover:text-ink hover:bg-surface-3',
            'focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-400',
          )}
        >
          ✕
        </button>
      </div>
    </div>
  );
}
